import { useState } from "react";
import { CheckCircle2, XCircle, Calendar, Plus, CalendarDays } from "lucide-react";
import { Button, Table, Tag, Modal, Input, message, Tooltip, Tabs, Form, Select, InputNumber } from "antd";
import type { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import {
  useLeaveList, useLeaveApprove, useLeaveReject,
  useHolidayEntitlementList, useHolidayEntitlementSave,
} from "../../../hooks/api/leave/hooks";
import { ILeaveRequest, IHolidayEntitlement } from "../../../hooks/api/leave/callbacks";
import { useFieldWorkerList } from "../../../hooks/api/field-workers/hooks";

const LEAVE_TYPE_COLOR: Record<string, string> = {
  annual: "blue", sick: "orange", maternity: "purple", paternity: "cyan",
  adoption: "geekblue", bereavement: "volcano", unpaid: "default", other: "default",
};

const STATUS_COLOR: Record<string, string> = { pending: "orange", approved: "green", rejected: "red" };

function RejectModal({ record, onClose }: { record: ILeaveRequest | null; onClose: () => void }) {
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const reject = useLeaveReject({
    onSuccess: () => { msgApi.success("Rejected."); form.resetFields(); onClose(); },
    onError: (e: any) => msgApi.error(e?.response?.data?.message || "Failed"),
  });
  return (
    <Modal open={!!record} onCancel={onClose} title="Reject Leave Request" destroyOnClose footer={null}>
      {ctx}
      {record && (
        <div>
          <p className="text-slate-600 text-sm mb-4">
            Rejecting leave for <strong>{record.worker_name ?? "worker"}</strong> — {dayjs(record.start_date).format("DD MMM")} to {dayjs(record.end_date).format("DD MMM YYYY")}
          </p>
          <Form form={form} layout="vertical">
            <Form.Item name="reason" label="Rejection Reason" rules={[{ required: true, message: "Reason required" }]}>
              <Input.TextArea rows={3} placeholder="Please provide a reason…" />
            </Form.Item>
          </Form>
          <div className="flex justify-end gap-2 mt-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button danger loading={reject.isPending} onClick={() => form.validateFields().then((v) => reject.mutate({ id: record.id, reason: v.reason }))}>
              Reject Request
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function LeaveTable({ statusFilter }: { statusFilter?: string }) {
  const [page, setPage] = useState(1);
  const [rejectRecord, setRejectRecord] = useState<ILeaveRequest | null>(null);
  const [msgApi, ctx] = message.useMessage();

  const { data, isLoading, refetchWithQuery } = useLeaveList({ page, limit: 20, ...(statusFilter ? { status: statusFilter } : {}) });
  const requests: ILeaveRequest[] = (data?.data ?? []) as ILeaveRequest[];
  const total = Number((data as any)?.extra?.total ?? 0);

  const approve = useLeaveApprove({
    onSuccess: (res) => {
      const conflicts = res?.data?.conflicting_jobs ?? [];
      if (conflicts.length) {
        msgApi.warning(
          `Approved. Warning: ${conflicts.length} job(s) still assigned to this worker during the leave period (${conflicts.map((j) => j.job_number).join(", ")}) — reassign them.`,
          8,
        );
      } else {
        msgApi.success("Approved.");
      }
      refetchWithQuery({ page, ...(statusFilter ? { status: statusFilter } : {}) });
    },
    onError: (e: any) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  const columns: ColumnType<ILeaveRequest>[] = [
    {
      title: "Worker", key: "worker",
      render: (_, r) => <div><div className="font-medium text-slate-800 text-sm">{r.worker_name ?? "—"}</div>{r.worker_email && <div className="text-xs text-slate-400">{r.worker_email}</div>}</div>,
    },
    { title: "Leave Type", dataIndex: "leave_type", key: "leave_type", render: (v) => <Tag color={LEAVE_TYPE_COLOR[v] ?? "default"}>{(v ?? "").replace(/_/g, " ")}</Tag> },
    {
      title: "Dates", key: "dates",
      render: (_, r) => <div className="text-sm"><div>{dayjs(r.start_date).format("DD MMM YYYY")}</div><div className="text-xs text-slate-400">to {dayjs(r.end_date).format("DD MMM YYYY")}</div></div>,
    },
    { title: "Days", dataIndex: "days_requested", key: "days_requested", render: (v) => <span className="font-medium">{v ?? "—"}</span> },
    { title: "Reason", dataIndex: "reason", key: "reason", render: (v) => <span className="text-xs text-slate-600 truncate max-w-[150px] block">{v ?? "—"}</span> },
    { title: "Status", dataIndex: "status", key: "status", render: (v) => <Tag color={STATUS_COLOR[v] ?? "default"}>{v}</Tag> },
    {
      title: "Actioned By", key: "approved_by",
      render: (_, r) => <div className="text-xs text-slate-500">{r.approved_by_name ?? (r.status === "pending" ? <span className="italic">Pending</span> : "—")}{r.decided_at && <div className="text-[10px]">{dayjs(r.decided_at).format("DD MMM YYYY")}</div>}</div>,
    },
    {
      title: "Actions", key: "actions",
      render: (_, r) => r.status !== "pending" ? null : (
        <div className="flex gap-1">
          <Tooltip title="Approve">
            <Button size="small" icon={<CheckCircle2 size={12} />} loading={approve.isPending} onClick={() => approve.mutate(r.id)} className="!text-emerald-600 !border-emerald-200" />
          </Tooltip>
          <Tooltip title="Reject">
            <Button size="small" icon={<XCircle size={12} />} danger type="text" onClick={() => setRejectRecord(r)} />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      {ctx}
      <Table dataSource={requests} columns={columns} rowKey="id" loading={isLoading}
        rowClassName={(r) => r.status === "pending" ? "bg-amber-50/20" : ""}
        pagination={{ current: page, pageSize: 20, total, showSizeChanger: false, showTotal: (t) => `${t} requests`, onChange: (p) => { setPage(p); refetchWithQuery({ page: p, ...(statusFilter ? { status: statusFilter } : {}) }); } }}
        scroll={{ x: 800 }} />
      <RejectModal record={rejectRecord} onClose={() => { setRejectRecord(null); refetchWithQuery({ page, ...(statusFilter ? { status: statusFilter } : {}) }); }} />
    </>
  );
}

function SetEntitlementModal({ open, onClose, record }: { open: boolean; onClose: () => void; record: IHolidayEntitlement | null }) {
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const { data: workerRes } = useFieldWorkerList({ page: 1, limit: 100 });
  const workers = (workerRes?.data as any[]) ?? [];

  const save = useHolidayEntitlementSave({
    onSuccess: () => { msgApi.success(record ? "Entitlement updated." : "Entitlement created."); form.resetFields(); onClose(); },
    onError: (e: any) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  return (
    <Modal
      open={open}
      destroyOnClose
      onCancel={onClose}
      title={record ? "Edit Holiday Entitlement" : "Set Holiday Entitlement"}
      width={480}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="save" type="primary" loading={save.isPending} onClick={() => form.submit()}>Save</Button>,
      ]}
    >
      {ctx}
      <Form
        form={form}
        layout="vertical"
        size="middle"
        className="pt-2"
        initialValues={record ? {
          user_id: record.user_id,
          tax_year: record.tax_year,
          total_days: Number(record.total_days),
          taken_days: Number(record.taken_days),
          carried_forward: Number(record.carried_forward),
        } : { tax_year: String(dayjs().year()) }}
        onFinish={(vals) => {
          save.mutate({
            user_id: vals.user_id,
            tax_year: String(vals.tax_year),
            total_days: vals.total_days,
            taken_days: vals.taken_days,
            carried_forward: vals.carried_forward,
          });
        }}
      >
        <Form.Item name="user_id" label="Worker" rules={[{ required: true, message: "Select a worker" }]}>
          <Select
            showSearch
            disabled={!!record}
            placeholder="Select worker…"
            filterOption={(v, o) => (o?.label as string)?.toLowerCase().includes(v.toLowerCase())}
            options={workers.map((w: any) => ({
              value: w.id,
              label: [w.first_name, w.last_name].filter(Boolean).join(" ") || w.email,
            }))}
          />
        </Form.Item>
        <Form.Item name="tax_year" label="Tax Year" rules={[{ required: true, message: "Tax year is required" }]}>
          <Input disabled={!!record} placeholder="e.g. 2026" />
        </Form.Item>
        <div className="grid grid-cols-3 gap-x-3">
          <Form.Item name="total_days" label="Total Days" rules={[{ required: true, message: "Required" }]}>
            <InputNumber className="w-full" min={0} step={0.5} />
          </Form.Item>
          <Form.Item name="taken_days" label="Taken Days">
            <InputNumber className="w-full" min={0} step={0.5} />
          </Form.Item>
          <Form.Item name="carried_forward" label="Carried Forward">
            <InputNumber className="w-full" min={0} step={0.5} />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}

function EntitlementsTable() {
  const [page, setPage] = useState(1);
  const [editRecord, setEditRecord] = useState<IHolidayEntitlement | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const { data, isLoading, refetchWithQuery } = useHolidayEntitlementList({ page, limit: 20 });
  const entitlements: IHolidayEntitlement[] = (data?.data ?? []) as IHolidayEntitlement[];
  const total = Number((data as any)?.extra?.total ?? 0);

  const columns: ColumnType<IHolidayEntitlement>[] = [
    { title: "Worker", key: "worker", render: (_, r) => r.worker_name ?? "—" },
    { title: "Tax Year", dataIndex: "tax_year", key: "tax_year", width: 100 },
    { title: "Total Days", dataIndex: "total_days", key: "total_days", width: 100, render: (v) => Number(v).toFixed(1) },
    { title: "Taken Days", dataIndex: "taken_days", key: "taken_days", width: 100, render: (v) => Number(v).toFixed(1) },
    {
      title: "Remaining", key: "remaining", width: 110,
      render: (_, r) => {
        const remaining = Number(r.total_days) + Number(r.carried_forward || 0) - Number(r.taken_days);
        return <span className={remaining < 0 ? "text-red-600 font-semibold" : "font-medium"}>{remaining.toFixed(1)}</span>;
      },
    },
    { title: "Carried Forward", dataIndex: "carried_forward", key: "carried_forward", width: 130, render: (v) => Number(v ?? 0).toFixed(1) },
    {
      title: "", key: "actions", width: 90,
      render: (_, r) => <Button size="small" onClick={() => { setEditRecord(r); setModalOpen(true); }}>Edit</Button>,
    },
  ];

  return (
    <>
      <div className="flex justify-end mb-3">
        <ButtonWrapper type="primary" icon={<Plus size={15} />} onClick={() => { setEditRecord(null); setModalOpen(true); }} className="flex items-center gap-2">
          Set Entitlement
        </ButtonWrapper>
      </div>
      <Table
        dataSource={entitlements}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        locale={{ emptyText: "No holiday entitlements set yet." }}
        pagination={{ current: page, pageSize: 20, total, showSizeChanger: false, showTotal: (t) => `${t} entitlements`, onChange: (p) => { setPage(p); refetchWithQuery({ page: p, limit: 20 }); } }}
        scroll={{ x: 760 }}
      />
      <SetEntitlementModal
        open={modalOpen}
        record={editRecord}
        onClose={() => { setModalOpen(false); setEditRecord(null); refetchWithQuery({ page, limit: 20 }); }}
      />
    </>
  );
}

const LeaveRequestsPage = () => {
  const { data: pendingData } = useLeaveList({ page: 1, limit: 1, status: "pending" });
  const totalPending = Number((pendingData as any)?.extra?.total ?? 0);

  return (
    <div className="p-6 min-h-screen">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
          <Calendar size={20} style={{ color: "var(--color-primary)" }} />
          Leave Requests
        </h1>
        <p className="text-slate-500 text-sm">{totalPending} pending approval</p>
      </div>
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
        <Tabs className="px-4 pt-2" items={[
          { key: "all", label: "All", children: <div className="pb-4"><LeaveTable /></div> },
          {
            key: "pending",
            label: <span className="flex items-center gap-1.5">Pending {totalPending > 0 && <span className="text-[10px] rounded-full px-1.5 py-0.5 text-white" style={{ background: "#f59e0b" }}>{totalPending}</span>}</span>,
            children: <div className="pb-4"><LeaveTable statusFilter="pending" /></div>,
          },
          { key: "approved", label: "Approved", children: <div className="pb-4"><LeaveTable statusFilter="approved" /></div> },
          { key: "rejected", label: "Rejected", children: <div className="pb-4"><LeaveTable statusFilter="rejected" /></div> },
          {
            key: "entitlements",
            label: <span className="flex items-center gap-1.5"><CalendarDays size={13} />Holiday Entitlements</span>,
            children: <div className="pb-4"><EntitlementsTable /></div>,
          },
        ]} />
      </div>
    </div>
  );
};

export default LeaveRequestsPage;
